﻿<template>
  <items-listing-layout class="all-tasks">
    <template #header-left>
      <!-- HEADER TITLE -->
      <listing-header 
        :is-loading="isLoadingInitialData"
        :is-custom-filter-submitting="isCustomFilterSubmitting"
        :entity-type="EEntities.PROJECTS"
        :default-filters="defaultFilterNavItems"
        :custom-filters="customFilterNavItems"
        :current-filter="currentFilterNavItem"
        :total-item-count="totalItemCount"
        title="All Tasks"
        @save-new-filter="(name) => createCustomFilter(name)"
        @update-custom-filter-name="(name) => updateCustomFilter(name)"
        @delete-custom-filter="(id) => deleteCustomFilter(id)"
        @change-filter="(id) => updateItemTraitSetFromFilterID(id)"
      />
    </template>
    <template v-if="hasEntities" #header-right>
      <div v-if="$screen.isTabletOrDesktop($mq)" class="all-tasks__header-options">
        <new-project-button v-if="$permissions.isNotGuest" size="medium" />
        <new-task-button size="medium" />
      </div>
    </template>
    <template #main-header>
      <!-- NO PROJECT EMPTY STATE -->
      <el-card v-if="!isLoadingInitialData && !hasEntities" class="all-tasks__no-projects">
        <h5>All tasks</h5>
        <template v-if="$permissions.isAdminOrOwner">
          <h2>Create your first project</h2>
          <p>Then, you'll be able to create tasks</p>
          <img :src="projectsImg" :srcset="`${projectsImg} 1x, ${projectsImg2x} 2x, ${projectsImg3x} 3x`">
          <new-project-button />
        </template>
        <template v-else>
          <h2>You don't belong to any projects yet</h2>
          <p>Once an account admin invites you to a project, you can view tasks here.</p>
          <img :src="projectsImg" :srcset="`${projectsImg} 1x, ${projectsImg2x} 2x, ${projectsImg3x} 3x`">
        </template>
      </el-card>
      <item-filters-panel 
        v-else
        v-model:search-term="itemTraitSet.searchTerm"
        v-model:selected-entity-ids="itemTraitSet.projectIDs"
        v-model:selected-assignee-ids="itemTraitSet.assigneeIDs"
        v-model:assigned-not-to-you="itemTraitSet.assignedNotToYou"
        v-model:selected-job-appointee-groupings-string="itemTraitSet.jobAppointeeGroupingsString"
        v-model:selected-creator-ids="itemTraitSet.creatorIDs"
        v-model:selected-status-ids="itemTraitSet.statusIDs"
        v-model:status-type="itemTraitSet.statusType"
        v-model:selected-priority-ids="itemTraitSet.priorityIDs"
        v-model:selected-due-type="itemTraitSet.dueType"
        v-model:selected-including-tag-ids="itemTraitSet.includingTagIDs"
        v-model:selected-excluding-tag-ids="itemTraitSet.notIncludingTagIDs"
        v-model:created-within-days="itemTraitSet.createdWithinDays"
        v-model:last-updated-within-days="itemTraitSet.lastUpdatedWithinDays"
        v-model:only-watching="itemTraitSet.onlyWatching"
        v-model:start-due-date="itemTraitSet.startDueDate"
        v-model:end-due-date="itemTraitSet.endDueDate"
        v-model:selected-sort="itemTraitSet.sort"
        :is-disabled="hasSelectedRows"
        :is-loading="isLoadingInitialData"
        :is-default-item-trait-set="isDefaultItemTraitSet"
        :entity-options="entities"
        :assignee-options="assignees"
        :job-options="jobs"
        :priority-options="priorities"
        :active-status-options="activeStatuses"
        :inactive-status-options="inactiveStatuses"
        :tag-options="tags"       
        :due-date-options="dueDateOptions"
        is-all-tasks
        @reset-filters="resetItemTraitSet"
      />
    </template>
    <template v-if="hasEntities" #main-content>
      <!-- TABLE -->
      <home-table
        ref="homeTable"
        :class="{'all-tasks__table-no-page-buffer': !hasPages}"
        :type="EEntities.PROJECTS"
        :table-data="results"
        :custom-fields="customFields"
        :is-loading="isLoadingResults"
        class="results"
        from-all
        @select-rows="updateSelectedRows"
      />
      <!-- PAGINATION -->
      <el-pagination
        v-if="hasPages"
        :page-size="itemsPerPage"
        :pager-count="5"
        :current-page="currentPage"
        :total="totalItemCount"
        background
        layout="prev, pager, next"
        class="all-tasks__pagination"
        @current-change="(page) => changePage(page)"
      />
    </template>
    <template #modals>
      <!-- BULK EDIT (DESKTOP) -->
      <bulk-edit-panel
        v-if="hasSelectedRows" 
        :selected-ids="selectedItemIDs"
        :selected-entity-ids="entityIDsForSelectedRows"
        :entity-type="EEntities.PROJECTS"
        @edit-canceled="($refs.homeTable as { clearBulkEdit(): void }).clearBulkEdit()"
        @edit-completed="reloadData()"
        @move-items-clicked="showMoveItemsModal=true"
        @add-comment-clicked="showAddCommentModal=true"
      />
      <!-- MOVE PROJECT -->
      <!-- Only will be accessible if all selected items are from a single project since
           move-items-clicked is only possibly emitted in that scenario-->
      <bulk-move-items-modal
        v-if="showMoveItemsModal"
        :is-convo-related="false"
        :entity-id="entityIDsForSelectedRows[0]"
        :selected-item-ids="selectedItemIDs"
        @edit-canceled="showMoveItemsModal=false"
        @edit-completed="reloadData();showMoveItemsModal=false;"
      />
      <!-- BULK ADD COMMENT -->
      <bulk-add-comment-modal
        v-if="showAddCommentModal"
        :is-convo-related="false"
        :selected-item-ids="selectedItemIDs"
        @edit-canceled="showAddCommentModal=false"
        @edit-completed="reloadData();showAddCommentModal=false;"
      />
      <!-- MOBILE NEW BUTTON -->
      <div v-if="$screen.isTabletOrMobile($mq) && hasResults" @click="RouteAssistant.goToGlobalCreateTask($router, $route.params.accountId)">
        <dd-icon-button
          :class="{'go-native-ios' : $screen.isGoNativeiOSMobileApp($mq)}"
          :color="ColorAssistant.ProjectColor" 
          icon="plus"
          class="all-tasks__create-button" 
          type="circle"
          title="Add task" 
        />
      </div>
    </template>
  </items-listing-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import dayjs from '@/helpers/dayjs'

/* Mixins */
import AllLandingMixin from '@/mixins/all-landing.mixin'

/* Components */
import projectsImg from '@/assets/img/entities/illustration-project-empty-state.png'
import projectsImg2x from '@/assets/img/entities/illustration-project-empty-state@2x.png'
import projectsImg3x from '@/assets/img/entities/illustration-project-empty-state@3x.png'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import TextAssistant from '@/helpers/text-assistant'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities, SortOptionValues } from '@/types'
import {
  IBreadcrumbItemMenuItem,
  IHomeTableItem,
} from '@/types/data-objects/items'
import { IItemTraitSet } from '@/types/data-objects/listing'
import {
  ICustomField,
  ITasksOutputAllFilterOptions,
  ITasksOutputSearchTask,
} from '@/api/types/response.types'
import {
  ITasksInputManageCustomFilter,
  ITasksInputUpdateCustomFilter,
} from '@/api/types/request.types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import CustomFiltersAPI from '@/api/modules/custom-filters.api'

export default defineComponent({
  name: 'AllTasks',

  components: {
    ItemsListingLayout,
  },

  extends: AllLandingMixin,

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      projectsImg,
      projectsImg2x,
      projectsImg3x,
      customFields: [] as ICustomField[],
    }
  },

  head: {
    title: 'All Tasks',
  },

  beforeUnmount() {
    this.endPolling()
  },

  async created() {
    this.isLoadingInitialData = true
    await Promise.all([this.fetchFilterOptions()])
    LocalStore.lastItemListRouteVisited = RouteAssistant.getAllTasksRoute(
      this.accountID,
    )
    this.startPolling()
    this.postCreated()
    this.isLoadingInitialData = false
  },

  methods: {
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      // A load that finishes after the user left would otherwise start a poller nothing clears
      if (this.isUnmounted) {
        return
      }
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async reloadData() {
      await Promise.all([this.fetchFilterOptions(), this.fetchResults(false)])
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await InternalProjectsAPI.get_MostRecentLastUpdatedEpochTime()
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await Promise.all([this.fetchFilterOptions(), this.fetchResults(false)])
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async fetchResults(showResultsLoading: boolean = true) {
      this.selectedItemIDs = []
      this.isLoadingResults = showResultsLoading
      const params = {
        page: this.currentPage,
        internal_project_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.projectIDs,
        ),
        priority_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.priorityIDs,
        ),
        status_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.statusIDs,
        ),
        status_type: this.itemTraitSet.statusType,
        assignee_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.assigneeIDs,
        ),
        assigned_not_to_you: this.itemTraitSet.assignedNotToYou,
        job_appointee_groupings_string: this.itemTraitSet
          .jobAppointeeGroupingsString,
        including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.includingTagIDs,
        ),
        not_including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.notIncludingTagIDs,
        ),
        start_due_date: this.itemTraitSet.startDueDate,
        end_due_date: this.itemTraitSet.endDueDate,
        due_type: this.itemTraitSet.dueType,
        sort: this.itemTraitSet.sort,
        search_term: this.itemTraitSet.searchTerm,
        only_watching: this.itemTraitSet.onlyWatching,
        created_within_days: this.itemTraitSet.createdWithinDays,
        last_updated_within_days: this.itemTraitSet.lastUpdatedWithinDays,
        creator_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.creatorIDs,
        ),
      }

      try {
        // If hiding the load, don't reset total item count so there
        // is no "pulse" on the dashboard filter picker
        if (showResultsLoading) {
          this.totalItemCount = null
        }
        // @ts-ignore
        const response = await TasksAPI.get_Search(
          params.page,
          params.internal_project_ids,
          params.creator_ids,
          params.sort,
          params.priority_ids,
          params.status_ids,
          params.status_type,
          params.assignee_ids,
          params.assigned_not_to_you,
          params.job_appointee_groupings_string,
          params.including_tag_ids,
          params.not_including_tag_ids,
          params.due_type,
          params.start_due_date,
          params.end_due_date,
          params.search_term,
          params.only_watching,
          params.created_within_days,
          params.last_updated_within_days,
        )

        // Store access epoch time
        this.lastAccessEpochTime = response.accessEpochTime

        // Pagination
        this.totalItemCount = response.totalTaskCount

        // Custom fields flagged to show on listings, across the projects in these results
        this.customFields = response.listCustomFields || []

        // Results
        // @ts-ignore
        this.results = response.listTasks.map(item =>
          this.mapResultsToTableFormat(item),
        )

        const breadCrumbItems = [] as IBreadcrumbItemMenuItem[]

        response.listTasks.forEach(t => {
          breadCrumbItems.push({
            itemID: t.id,
            entityID: t.project.id,
            entityName: t.project.name,
            entityType: EEntities.PROJECTS,
            title: t.title,
            refNumber: t.refNumber,
          } as IBreadcrumbItemMenuItem)
        })

        BreadcrumbAssistant.cacheBreadcrumbItemMenuItems(
          breadCrumbItems,
        )
      } catch (error) {
        LocalStore.clearAllTasksSavedQuery(this.accountID)
        this.$confirm(
          `Let's reset your project filters so you can try again.`,
          {
            dangerouslyUseHTMLString: true,
            center: true,
            type: 'warning',
            title: 'This filter is no longer available',
          },
        ).then(() => {
          this.itemTraitSet.projectIDs = []
        })
      }
      this.isLoadingResults = false
    },

    async fetchFilterOptions() {
      try {
        const response = await TasksAPI.get_All_FilterOptions()
        this.hydrateFilterOptions(response)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    hydrateFilterOptions(data: ITasksOutputAllFilterOptions) {
      this.hydrateFilterOptionsForListing_Base(data)
      this.entities = data.listInternalProjects

      this.customFilterNavItems = data.listCustomFilters.map(c => ({
        value: ItemFilterAssistant.getCustomFilterIdentifier(c.id),
        label: c.name,
        icon: 'filter',
      }))
      this.customFilterItemTraitSets = data.listCustomFilters.map(c => ({
        id: c.id,
        itemTraitSet: {
          priorityIDs: c.listPriorityIDs ? c.listPriorityIDs : [],
          statusIDs: c.listStatusIDs ? c.listStatusIDs : [],
          statusType: c.statusType ? c.statusType : '',
          assigneeIDs: c.listAssigneeIDs ? c.listAssigneeIDs : [],
          assignedNotToYou: c.assignedNotToYou,
          jobAppointeeGroupingsString: ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
            c.listJobAppointeeGroupings,
          ),
          dueType: c.dueType ? c.dueType : null,
          includingTagIDs: c.listIncludingTagIDs ? c.listIncludingTagIDs : [],
          notIncludingTagIDs: c.listNotIncludingTagIDs
            ? c.listNotIncludingTagIDs
            : [],
          startDueDate: c.startDueDate
            ? dayjs(c.startDueDate).format('YYYY-MM-DD')
            : null,
          endDueDate: c.endDueDate
            ? dayjs(c.endDueDate).format('YYYY-MM-DD')
            : null,
          searchTerm: c.searchTerm ? c.searchTerm : '',
          onlyWatching: c.onlyWatching,
          createdWithinDays: c.createdWithinDays,
          lastUpdatedWithinDays: c.lastUpdatedWithinDays,
          creatorIDs: c.listCreatorIDs ? c.listCreatorIDs : [],
          customerIDs: [], // Never used, default it so the comparison is equal in isItemTraitSetEquivalent()
          isSpam: false, // Never used, so default it so the comparison is equal in isItemTraitSetEquivalent()
          sort: SortOptionValues.UPDATED_RECENT,
          projectIDs: c.listInternalProjectIDs ? c.listInternalProjectIDs : [],
          mailboxIDs: [], // Unused, default it so the comparison is equal in isItemTraitSetEquivalent()
          customFieldFiltersString: '', // Unused, default it so the comparison is equal in isItemTraitSetEquivalent()
        } as IItemTraitSet,
      }))
    },

    async createCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCreatorIDs: this.itemTraitSet.creatorIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          listInternalProjectIDs: this.itemTraitSet.projectIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as ITasksInputManageCustomFilter
        const response = await TasksAPI.post_CreateCustomFilter(formData)
        await this.fetchFilterOptions()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async updateCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCreatorIDs: this.itemTraitSet.creatorIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          listInternalProjectIDs: this.itemTraitSet.projectIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          onlyWatching: this.itemTraitSet.onlyWatching,
        } as ITasksInputUpdateCustomFilter
        const response = await TasksAPI.put_UpdateCustomFilter(
          ItemFilterAssistant.getIDOfFilterIdentifier(
            this.currentFilterNavItem.value,
          ),
          formData,
        )
        await this.fetchFilterOptions()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    handleDeleteCustomFilter(identifier: string) {
      this.$confirm(
        `<strong>This cannot be undone.</strong>
        <br>Are you sure you want to delete this?`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: 'Delete this custom filter?',
        },
      ).then(() => {
        this.deleteCustomFilter(identifier)
      })
    },

    async deleteCustomFilter(identifier: string) {
      try {
        const response = await CustomFiltersAPI.delete_Delete(
          ItemFilterAssistant.getIDOfFilterIdentifier(identifier),
        )

        this.fetchFilterOptions()

        if (this.currentFilterNavItem.value !== identifier) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    mapResultsToTableFormat(item: ITasksOutputSearchTask): IHomeTableItem {
      return {
        ...item,
        parent: item.project,
        entityType: EEntities.PROJECTS,
      }
    },
  },
})
</script>

<style lang="scss">
.all-tasks {
  &__table-no-page-buffer {
    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  &__create-button {
    position: fixed;
    right: 15px;
    bottom: 20px;

    @include media-breakpoint-down(sm) {
      bottom: 70px;
      &.go-native-ios {
        bottom: 100px;
      }
    }
  }

  &__header-options {
    display: flex;
    @include flex-row-horizontal-spacing(8px);
  }

  &__no-projects {
    margin-top: 20px;
    padding: 20px 0;
    .el-card__body {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: auto;
      > img {
        padding-bottom: 20px;
      }
    }
  }

  &__pagination {
    margin-top: 24px;
    text-align: center;

    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  .container.main-container {
    /* For home, "mobile" switch happens at medium */
    @include media-breakpoint-down(md) {
      max-width: 100%;
    }
  }
}
</style>
